// Catálogo en grilla (decisión D49): una categoría (`/categoria/:nombre`) o todo el catálogo con el buscador (`/buscar`).
// "‹ Todas las categorías", el título con su ícono, el buscador (filtra mientras se escribe), las categorías en chips
// que se deslizan, cuántos productos hay y la grilla (2 columnas en el celular, hasta 5 en computadora).
import { cx, Icono } from '@reparto/ui';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { Boton, ErrorCarga, Vacio } from '../componentes/base';
import { EsqueletoCategorias, EsqueletoChips, EsqueletoGrilla } from '../componentes/Esqueletos';
import { Pantalla } from '../componentes/Marco';
import { iconoDeProducto } from '../componentes/Miniatura';
import { CLASES_GRILLA, TarjetaProducto } from '../componentes/TarjetaProducto';
import { busquedaEfectiva, useAlLlegar, useCatalogo } from '../datos/catalogo';
import { useConsulta, useDemorado } from '../datos/consultas';
import { plural } from '../datos/formato';
import { claveFuente, traerCategorias, useFuente, useReglas } from '../datos/fuente';
import { BuscadorCatalogo, rutaCategoria } from './Inicio';

export function Catalogo() {
  const { nombre } = useParams();
  const categoria = nombre ?? null;
  const [params, setParams] = useSearchParams();
  const inicial = params.get('q') ?? '';
  const [texto, setTexto] = useState(inicial);
  const q = busquedaEfectiva(useDemorado(texto, 350));
  const fuente = useFuente();
  const reglas = useReglas();
  const cats = useConsulta(fuente ? `categorias:${claveFuente(fuente)}` : null, () => traerCategorias(fuente!));
  const c = useCatalogo(fuente, q, categoria);
  const centinela = useAlLlegar(c.cargarMas, c.hayMas && !c.error && !c.cargandoMas);

  // Otra categoría: la búsqueda es la de su dirección (los chips llegan sin búsqueda).
  useEffect(() => setTexto(params.get('q') ?? ''), [categoria]);

  // La búsqueda queda en la dirección (volver atrás la conserva).
  const primera = useRef(true);
  useEffect(() => {
    if (primera.current) {
      primera.current = false;
      return;
    }
    setParams(q ? { q } : {}, { replace: true });
  }, [q]);

  const lista = cats.datos?.datos ?? [];
  const deCategoria = categoria ? lista.find((x) => x.nombre === categoria) : undefined;
  const cuantos =
    !q && deCategoria
      ? plural(deCategoria.articulos, 'producto', 'productos')
      : c.cargando
        ? null
        : `${c.hayMas ? 'Más de ' : ''}${plural(c.articulos.length, 'producto', 'productos')}`;

  return (
    <Pantalla amplia>
      <div className="px-4 pt-4 pb-8">
        <Link
          to={categoria ? '/categorias' : '/'}
          className="-ml-2 inline-flex min-h-11 items-center gap-1 rounded-full px-2 text-[15px] font-medium text-stone-600 hover:bg-stone-200/60"
        >
          <Icono nombre="chevron_left" tamano={22} />
          {categoria ? 'Todas las categorías' : 'Inicio'}
        </Link>
        <h1 className="mt-1 flex items-center gap-2.5 text-[28px] leading-tight font-bold text-stone-900 md:text-[32px]">
          <Icono
            nombre={categoria ? iconoDeProducto(categoria) : 'search'}
            tamano={34}
            className="shrink-0 text-primary-700"
          />
          <span className="min-w-0">{categoria ?? 'Todo el catálogo'}</span>
        </h1>
        <BuscadorCatalogo
          key={categoria ?? ''}
          inicial={inicial}
          alBuscar={setTexto}
          enfocar={!categoria && !inicial}
        />

        {lista.length ? (
          <nav
            aria-label="Categorías"
            className="sin-scroll relative -mx-4 mt-4 flex gap-2 overflow-x-auto px-4 pb-1 lg:mx-0 lg:flex-wrap lg:overflow-visible lg:px-0"
          >
            <Chip activa={!categoria} a={q ? `/buscar?q=${encodeURIComponent(q)}` : '/buscar'} icono="apps">
              Todo el catálogo
            </Chip>
            {lista.map((x) => (
              <Chip
                key={x.nombre}
                activa={x.nombre === categoria}
                a={rutaCategoria(x.nombre)}
                icono={iconoDeProducto(x.nombre)}
              >
                {x.nombre}
              </Chip>
            ))}
          </nav>
        ) : cats.cargando ? (
          <EsqueletoChips />
        ) : null}

        <div className="mt-5 flex items-baseline justify-between gap-3">
          <h2 className="text-xl font-bold text-stone-900">{q ? `Resultados para “${q}”` : 'Productos'}</h2>
          {cuantos ? <span className="shrink-0 text-[15px] text-stone-600">{cuantos}</span> : null}
        </div>

        <section className="mt-3" aria-busy={c.cargando || c.cargandoMas} aria-label="Productos">
          {c.cargando ? (
            <EsqueletoGrilla />
          ) : c.error && !c.articulos.length ? (
            <ErrorCarga mensaje={c.error} sinRed={c.sinRed} onReintentar={c.recargar} />
          ) : !c.articulos.length ? (
            <Vacio
              icono="search_off"
              titulo={q ? `No encontramos “${q}”` : 'No hay productos acá'}
              texto={q ? 'Probá con otra palabra, más corta o sin la marca.' : 'Probá con otra categoría.'}
            >
              <Link
                to="/buscar"
                className="inline-flex min-h-12 items-center justify-center rounded-full border-2 border-stone-300 px-5 font-semibold text-stone-800 hover:bg-stone-100"
              >
                Ver todo el catálogo
              </Link>
            </Vacio>
          ) : (
            <>
              <ul className={CLASES_GRILLA}>
                {c.articulos.map((a) => (
                  <li key={a.id}>
                    <TarjetaProducto a={a} permitirSinStock={reglas.permitirSinStock} />
                  </li>
                ))}
              </ul>
              <div ref={centinela} />
              {c.cargandoMas ? (
                <EsqueletoGrilla tarjetas={2} className="mt-3" />
              ) : c.error ? (
                <div className="mt-3">
                  <ErrorCarga compacto mensaje={c.error} sinRed={c.sinRed} onReintentar={() => c.reintentarMas()} />
                </div>
              ) : c.hayMas ? (
                <Boton variante="secundario" bloque className="mt-4" onClick={c.cargarMas}>
                  Ver más productos
                </Boton>
              ) : null}
            </>
          )}
        </section>
      </div>
    </Pantalla>
  );
}

function Chip({ activa, a, icono, children }: { activa: boolean; a: string; icono: string; children: string }) {
  return (
    <Link
      to={a}
      replace
      aria-current={activa ? 'page' : undefined}
      className={cx(
        'inline-flex h-11 shrink-0 items-center gap-1.5 rounded-full border px-4 text-[15px] font-medium whitespace-nowrap',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600',
        activa
          ? 'border-primary-600 bg-primary-600 text-sobre-marca'
          : 'border-stone-300 bg-white text-stone-800 hover:border-primary-400 hover:bg-primary-50',
      )}
    >
      <Icono nombre={icono} tamano={20} />
      {children}
    </Link>
  );
}

/** Todas las categorías, con un buscador para encontrar una. */
export function Categorias() {
  const fuente = useFuente();
  const cats = useConsulta(fuente ? `categorias:${claveFuente(fuente)}` : null, () => traerCategorias(fuente!));
  const [buscar, setBuscar] = useState('');
  const normal = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
  const visibles = (cats.datos?.datos ?? []).filter((c) => normal(c.nombre).includes(normal(buscar.trim())));
  return (
    <Pantalla titulo="Categorías" volver="/" amplia>
      <div className="px-4 pb-8">
        <label className="mt-4 flex min-h-14 items-center gap-2.5 rounded-2xl bg-white px-4 shadow-sm ring-1 ring-stone-200 focus-within:ring-2 focus-within:ring-primary-600">
          <Icono nombre="search" tamano={24} className="shrink-0 text-stone-500" />
          <input
            type="search"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            aria-label="Buscar una categoría"
            placeholder="Buscar una categoría"
            className="min-w-0 flex-1 bg-transparent py-3 text-[17px] text-stone-900 placeholder:text-stone-500 focus:outline-none"
          />
        </label>
        {cats.cargando ? (
          <EsqueletoCategorias />
        ) : cats.error && !cats.datos ? (
          <div className="mt-4">
            <ErrorCarga mensaje={cats.error} sinRed={cats.sinRed} onReintentar={() => void cats.recargar()} />
          </div>
        ) : (
          <>
            <p className="mt-3 text-[15px] text-stone-600" role="status">
              {plural(visibles.length, 'categoría', 'categorías')}
            </p>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {visibles.map((c) => (
                <Link
                  key={c.nombre}
                  to={rutaCategoria(c.nombre)}
                  className="flex min-h-32 flex-col rounded-2xl border border-stone-200 bg-white shadow-sm p-4 hover:border-primary-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                >
                  <Icono nombre={iconoDeProducto(c.nombre)} tamano={32} className="mb-3 text-primary-700" />
                  <span className="font-semibold text-stone-900">{c.nombre}</span>
                  <span className="mt-1 flex items-center justify-between gap-2 text-[15px] text-stone-600">
                    {plural(c.articulos, 'producto', 'productos')}
                    <Icono nombre="chevron_right" tamano={20} />
                  </span>
                </Link>
              ))}
            </div>
            {!visibles.length ? (
              <div className="mt-6 rounded-2xl bg-stone-100 p-5 text-center">
                <p className="text-stone-800">No encontramos esa categoría.</p>
                <button
                  type="button"
                  onClick={() => setBuscar('')}
                  className="mt-2 min-h-11 font-semibold text-primary-700"
                >
                  Ver todas las categorías
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </Pantalla>
  );
}
